import { Inject, Injectable } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import {
  PersonalDetailsForm,
  PersonalDetailsFormConfig,
  PersonalDetailsFormField,
} from '@checkout/util-models';
import { AddressFormBuilder } from '@domgen/dgx-fe-forms/address';
import { FormControlValidators } from '@domgen/dgx-fe-forms/shared';
import {
  COUNTRY_ISO_NUMBER,
  CountryIsoNumber,
  TranslateService,
} from '@domgen/dgx-fe-i18n';
import { FeaturePersonalDetailsTranslationDictionary } from '../../models/i18n/feature-personal-details-dictionary.interface';

@Injectable()
export class PersonalDetailsFormBuilder {
  constructor(
    @Inject(COUNTRY_ISO_NUMBER)
    private readonly countryIsoNumber: CountryIsoNumber,
    private readonly addressFormBuilder: AddressFormBuilder,
    private readonly formBuilder: FormBuilder,
    private readonly translateService: TranslateService<FeaturePersonalDetailsTranslationDictionary>
  ) {}

  build(config: PersonalDetailsFormConfig): FormGroup<PersonalDetailsForm> {
    return this.formBuilder.group(this.buildFormConfig(config));
  }

  private buildFormConfig(
    config: PersonalDetailsFormConfig
  ): PersonalDetailsForm {
    const { cmsData, initialValue } = config;

    return {
      [PersonalDetailsFormField.TitleId]: new FormControl(
        {
          value: initialValue?.titleId ?? null,
          disabled: Boolean(cmsData.titleIdField.fieldHidden),
        },
        Validators.compose([
          FormControlValidators.requiredConditionally(
            cmsData.titleIdField.fieldRequired,
            this.translateService.translate(
              'PERSONAL_DETAILS_FORM_TITLE_ID_ERROR_REQUIRED'
            )
          ),
        ])
      ),
      [PersonalDetailsFormField.FirstName]: new FormControl(
        initialValue?.firstName ?? null,
        Validators.compose([
          FormControlValidators.required(
            this.translateService.translate(
              'PERSONAL_DETAILS_FORM_FIRST_NAME_ERROR_REQUIRED'
            )
          ),
          FormControlValidators.alphabetical(
            this.translateService.translate(
              'PERSONAL_DETAILS_FORM_FIRST_NAME_ERROR_ALPHABETICAL'
            )
          ),
          FormControlValidators.maximumLength(
            15,
            this.translateService.translate(
              'PERSONAL_DETAILS_FORM_FIRST_NAME_ERROR_MAX_LENGTH',
              { maxLength: 15 }
            )
          ),
        ])
      ),
      [PersonalDetailsFormField.LastName]: new FormControl(
        initialValue?.lastName ?? null,
        Validators.compose([
          FormControlValidators.required(
            this.translateService.translate(
              'PERSONAL_DETAILS_FORM_LAST_NAME_ERROR_REQUIRED'
            )
          ),
          FormControlValidators.alphabetical(
            this.translateService.translate(
              'PERSONAL_DETAILS_FORM_LAST_NAME_ERROR_ALPHABETICAL'
            )
          ),
          FormControlValidators.maximumLength(
            20,
            this.translateService.translate(
              'PERSONAL_DETAILS_FORM_LAST_NAME_ERROR_MAX_LENGTH',
              { maxLength: 20 }
            )
          ),
        ])
      ),
      [PersonalDetailsFormField.Address]:
        this.addressFormBuilder.buildAddressOverrideForm(initialValue?.address),
      [PersonalDetailsFormField.EmailAddress]: new FormControl(
        initialValue?.emailAddress ?? null,
        Validators.compose([
          FormControlValidators.required(
            this.translateService.translate(
              'PERSONAL_DETAILS_FORM_EMAIL_ADDRESS_ERROR_REQUIRED'
            )
          ),
          FormControlValidators.email(
            this.translateService.translate(
              'PERSONAL_DETAILS_FORM_EMAIL_ADDRESS_ERROR_EMAIL'
            )
          ),
          FormControlValidators.maximumLength(
            50,
            this.translateService.translate(
              'PERSONAL_DETAILS_FORM_EMAIL_ADDRESS_ERROR_MAX_LENGTH',
              { maxLength: 50 }
            )
          ),
        ])
      ),

      [PersonalDetailsFormField.TelephoneNumber]: new FormControl(
        initialValue?.telephoneNumber ?? null,
        Validators.compose([
          FormControlValidators.required(
            this.translateService.translate(
              'PERSONAL_DETAILS_FORM_TELEPHONE_NUMBER_ERROR_REQUIRED'
            )
          ),
          FormControlValidators.telephone(
            this.countryIsoNumber,
            this.translateService.translate(
              'PERSONAL_DETAILS_FORM_TELEPHONE_NUMBER_ERROR_PATTERN'
            )
          ),
        ])
      ),
    };
  }
}
